import React, { useEffect, useState, useContext } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container, Typography, Button, Box, CircularProgress, Alert, 
  Card, CardContent, Grid, Paper, Divider, Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { AuthContext } from '../context/AuthContext';
import { bunkeringApi } from '../api/bunkering';
import { documentsApi } from '../api/documents';
import DocumentTable from '../components/DocumentTable';
import UploadDocument from '../components/UploadDocument';

export default function BunkeringDetail() {
  const { id: bunkeringId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [bunkering, setBunkering] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [documentsLoading, setDocumentsLoading] = useState(false);

  useEffect(() => {
    fetchBunkeringDetails();
  }, [bunkeringId]);

  const fetchBunkeringDetails = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await bunkeringApi.getBunkering(bunkeringId);
      const bunkeringData = response.data.data;
      setBunkering(bunkeringData);
      
      // Set documents from the bunkering response (includes documents array)
      if (bunkeringData.documents) {
        setDocuments(bunkeringData.documents);
      } else {
        // Fallback: fetch documents separately if not included
        await fetchDocuments();
      }
      
    } catch (err) {
      console.error('Failed to load bunkering details:', err);
      if (err.response && err.response.data) {
        setError(err.response.data.detail || 'Failed to load bunkering details.');
      } else {
        setError('Failed to load bunkering details.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    setDocumentsLoading(true);
    
    try {
      const response = await documentsApi.getBunkeringDocuments(bunkeringId);
      setDocuments(response.data.data || []);
    } catch (err) {
      console.error('Failed to load documents:', err);
      // Don't show error for documents as it's secondary data
    } finally {
      setDocumentsLoading(false);
    }
  };

  const handleUploadSuccess = (newDocument) => {
    // Refresh documents list after successful upload
    fetchDocuments();
    
    // Update bunkering document count if available
    if (bunkering) {
      setBunkering(prev => ({
        ...prev,
        document_count: (prev.document_count || 0) + 1
      }));
    }
  };


  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Check user permissions
  const canUpload = user && (user.role === 'SHIPOWNER' || user.role === 'SUPPLIER');
  const isReadOnly = user && user.role === 'VERIFIER';

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          {error}
        </Alert>
        <Box sx={{ mt: 2 }}>
          <Button
            component={RouterLink}
            to="/bunkerings"
            startIcon={<ArrowBackIcon />}
            variant="outlined"
          >
            Back to Bunkering Events
          </Button>
        </Box>
      </Container>
    );
  }

  if (!bunkering) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="warning">
          Bunkering event not found.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          component={RouterLink}
          to="/bunkerings"
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          sx={{ mb: 2 }}
        >
          Back to Bunkering Events
        </Button>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <DirectionsBoatIcon sx={{ fontSize: 32, color: '#1565c0' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Bunkering Event Details
          </Typography>
        </Box>
      </Box>

      {/* Bunkering Information */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DirectionsBoatIcon />
            Event Information
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Vessel
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {bunkering.vessel_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  IMO: {bunkering.vessel_imo}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Bunkering Date
                </Typography>
                <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarTodayIcon fontSize="small" />
                  {formatDate(bunkering.bunkering_date)}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Created By
                </Typography>
                <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon fontSize="small" />
                  {bunkering.created_by?.name || 'Unknown'}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Created At
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(bunkering.created_at)}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Document Count
                </Typography>
                <Chip 
                  label={`${documents.length || 0} documents`}
                  color={documents.length > 1 ? 'success' : 'default'}
                  icon={<InsertDriveFileIcon />}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Documents Table */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InsertDriveFileIcon />
            Documents
          </Typography>
          
          {isReadOnly && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You have read-only access to this bunkering event and its documents.
            </Alert>
          )}
          
          {documentsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DocumentTable
              documents={documents}
            />
          )}
        </CardContent>
      </Card>

      {/* Upload Additional Documents (only for Shipowner/Supplier) */}
      {canUpload && !isReadOnly && (
          <UploadDocument
              bunkeringId={bunkeringId}
              onUploadSuccess={handleUploadSuccess}
          />
      )}
    </Container>
  );
}